import { V4_API_PREFIX } from "./api.constants";
import { DatePreset, TimePreset } from "../interfaces/geo-analytics";
import { EServiceCategory } from "./product.constants";
export { GeoLayerType } from "./geo-layer-type.enum";

export const GEO_ENDPOINTS = {
  CITIES: `/${V4_API_PREFIX}/cities`,
  CITY_BY_ID: (cityId: string) => `/${V4_API_PREFIX}/cities/${cityId}`,
  CITY_BY_CODE: (code: string) => `/${V4_API_PREFIX}/cities/code/${code}`,
  SUB_CITIES: `/${V4_API_PREFIX}/sub-cities`,
  SUB_CITY_BY_ID: (subCityId: string) => `/${V4_API_PREFIX}/sub-cities/${subCityId}`,
  HUBS: `/${V4_API_PREFIX}/hubs`,
  HOTSPOTS: `/${V4_API_PREFIX}/hotspots`,
  GEO_ANALYTICS: `/${V4_API_PREFIX}/geo-analytics/geojson`,
};

/**
 * Enum for geo entity status in the domain layer
 */
export enum EGeoStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  PENDING = "PENDING",
  DELETED = "DELETED",
}

/**
 * Map default configuration
 */
export const GEO_MAP_DEFAULTS = {
  CENTER: [28.6139, 77.2090] as const, // Delhi, India
  ZOOM: 11,
  MIN_HEIGHT: 600,
} as const;

/**
 * Color constants for geo analytics visualization.
 * Matches backend structure in @v4/common/constants/geo-analytics.constants.ts
 * All colors are defined here to avoid duplication and ensure consistency.
 *
 * IMPORTANT: Colors must match backend values exactly to ensure consistency.
 * Backend reference: src/v4/common/constants/geo-analytics.constants.ts
 */
export const GEO_ANALYTICS_COLORS = {
  /** Availability layer colors (discrete thresholds) - matches backend exactly */
  AVAILABILITY: {
    HIGH: "#00FF00", // Green - matches backend GEO_ANALYTICS_COLORS.AVAILABILITY.HIGH
    MEDIUM: "#eab308", // Yellow - matches backend GEO_ANALYTICS_COLORS.AVAILABILITY.MEDIUM
    LOW: "#FF0000", // Red - matches backend GEO_ANALYTICS_COLORS.AVAILABILITY.LOW
    NO_PARTNER: "#8B0000", // Dark red - for entities with no partners attached
  },
  /** Demand heatmap gradient colors - matches backend exactly */
  DEMAND: {
    LOW: "#90EE90", // Light green (low demand) - matches backend GEO_ANALYTICS_COLORS.DEMAND.LOW
    HIGH: "#00CED1", // Blue-green / Dark turquoise (high demand) - matches backend GEO_ANALYTICS_COLORS.DEMAND.HIGH
  },
  /** Common display colors */
  STROKE: "#000000", // Black stroke color - matches backend
  /** Boundary layer colors (for stroke-only visualization) */
  BOUNDARY: {
    CITY: "#4169E1", // Royal blue - matches backend
    SUBCITY: "#32CD32", // Lime green - matches backend
    HUB: "#FF6347", // Tomato red - matches backend
  },
  /** Default/fallback colors */
  DEFAULT: "#808080", // Gray
} as const;

/**
 * Availability state colors mapping.
 * Maps AvailabilityState enum values to colors.
 * Used for visual differentiation between no partner, low, medium, and high availability.
 */
export const GEO_AVAILABILITY_STATE_COLORS = {
  NO_PARTNER: GEO_ANALYTICS_COLORS.AVAILABILITY.NO_PARTNER, // Dark red - no partners attached
  LOW: GEO_ANALYTICS_COLORS.AVAILABILITY.LOW, // Red - availability < 30%
  MEDIUM: GEO_ANALYTICS_COLORS.AVAILABILITY.MEDIUM, // Yellow - availability 30-70%
  HIGH: GEO_ANALYTICS_COLORS.AVAILABILITY.HIGH, // Green - availability > 70%
} as const;

/**
 * Availability layer colors (legacy - use GEO_ANALYTICS_COLORS.AVAILABILITY)
 * @deprecated Use GEO_ANALYTICS_COLORS.AVAILABILITY instead
 */
export const GEO_AVAILABILITY_COLORS = {
  NO_PARTNER: GEO_ANALYTICS_COLORS.AVAILABILITY.NO_PARTNER,
  HIGH: GEO_ANALYTICS_COLORS.AVAILABILITY.HIGH,
  MEDIUM: GEO_ANALYTICS_COLORS.AVAILABILITY.MEDIUM,
  LOW: GEO_ANALYTICS_COLORS.AVAILABILITY.LOW,
  DEFAULT: GEO_ANALYTICS_COLORS.DEFAULT,
  /**
   * Availability thresholds (as per refactor documentation):
   * - LOW: < 30%
   * - MEDIUM: 30-70%
   * - HIGH: > 70%
   */
  THRESHOLDS: {
    HIGH: 70,
    MEDIUM: 30, // Updated from 40 to 30 per refactor documentation
  },
} as const;

/**
 * Demand layer colors (legacy - use GEO_ANALYTICS_COLORS.DEMAND)
 * @deprecated Use GEO_ANALYTICS_COLORS.DEMAND instead
 */
export const GEO_DEMAND_COLORS = {
  HIGH: GEO_ANALYTICS_COLORS.DEMAND.HIGH,
  LOW: GEO_ANALYTICS_COLORS.DEMAND.LOW,
  DEFAULT: GEO_ANALYTICS_COLORS.DEMAND.HIGH, // Default to high demand color
} as const;

/**
 * Color scale configuration for geo analytics visualization.
 * Matches backend ColorScaleConfig interface.
 */
export interface IColorScaleConfig {
  high: { threshold: number; color: string };
  medium: { threshold: number; color: string };
  low: { threshold: number; color: string };
}

/**
 * Default color scales for availability layer.
 * Uses color constants to ensure consistency with backend.
 */
export const GEO_ANALYTICS_COLOR_SCALES = {
  AVAILABILITY: {
    high: { threshold: 70, color: GEO_ANALYTICS_COLORS.AVAILABILITY.HIGH },
    medium: { threshold: 40, color: GEO_ANALYTICS_COLORS.AVAILABILITY.MEDIUM },
    low: { threshold: 0, color: GEO_ANALYTICS_COLORS.AVAILABILITY.LOW },
  },
} as const satisfies Record<string, IColorScaleConfig>;

/**
 * Default display properties for geo analytics features.
 * Matches backend GEO_ANALYTICS_DISPLAY_DEFAULTS structure.
 */
export const GEO_ANALYTICS_DISPLAY_DEFAULTS = {
  opacity: 0.7,
  strokeColor: GEO_ANALYTICS_COLORS.STROKE,
  strokeWidth: 2,
} as const;

/**
 * Marker styles for different layer types.
 * Uses GEO_ANALYTICS_DISPLAY_DEFAULTS for consistency.
 */
export const GEO_MARKER_STYLES = {
  AVAILABILITY: {
    radius: 3,
    fillOpacity: 0.6,
    weight: GEO_ANALYTICS_DISPLAY_DEFAULTS.strokeWidth,
    opacity: 0.8,
  },
  DEMAND: {
    radius: 3,
    fillOpacity: 0.5,
    weight: GEO_ANALYTICS_DISPLAY_DEFAULTS.strokeWidth,
    opacity: 0.9,
  },
  DEFAULT: {
    radius: 3,
    fillOpacity: GEO_ANALYTICS_DISPLAY_DEFAULTS.opacity,
    weight: GEO_ANALYTICS_DISPLAY_DEFAULTS.strokeWidth,
    opacity: GEO_ANALYTICS_DISPLAY_DEFAULTS.opacity,
  },
  HOVER: {
    radius: 4,
    weight: GEO_ANALYTICS_DISPLAY_DEFAULTS.strokeWidth,
    opacity: 1,
  },
} as const;

/**
 * Layer styles for polygons.
 * Uses GEO_ANALYTICS_DISPLAY_DEFAULTS for consistency.
 */
export const GEO_LAYER_STYLES = {
  AVAILABILITY: {
    fillOpacity: 0.6,
    weight: GEO_ANALYTICS_DISPLAY_DEFAULTS.strokeWidth,
    opacity: 0.8,
  },
  DEMAND: {
    fillOpacity: 0.5,
    weight: GEO_ANALYTICS_DISPLAY_DEFAULTS.strokeWidth,
    opacity: 0.9,
  },
  DEFAULT: {
    fillColor: GEO_ANALYTICS_COLORS.DEFAULT,
    fillOpacity: GEO_ANALYTICS_DISPLAY_DEFAULTS.opacity,
    color: GEO_ANALYTICS_COLORS.DEFAULT,
    weight: GEO_ANALYTICS_DISPLAY_DEFAULTS.strokeWidth,
    opacity: GEO_ANALYTICS_DISPLAY_DEFAULTS.opacity,
  },
} as const;

/**
 * Leaflet z-index configuration
 */
export const GEO_MAP_Z_INDEX = {
  CONTAINER: 0,
  CONTROLS: 1,
  HOVER_CARD: 10,
  ZOOM_INDICATOR: 1000,
} as const;

/**
 * Zoom level indicator styling constants
 */
export const GEO_ZOOM_INDICATOR_STYLES = {
  POSITION: {
    TOP: "10px",
    RIGHT: "10px",
  },
  BACKGROUND_COLOR: "rgba(255, 255, 255, 0.9)",
  PADDING: "8px 12px",
  BORDER_RADIUS: "4px",
  BOX_SHADOW: "0 2px 4px rgba(0,0,0,0.2)",
  FONT_SIZE: "14px",
  TEXT_COLOR: "#333",
} as const;

/**
 * Boundary layer styling constants
 */
export const GEO_BOUNDARY_STYLES = {
  WEIGHT: 3,
  OPACITY: 1,
} as const;

/**
 * Default fallback values for markers when properties cannot be parsed.
 * Uses GEO_ANALYTICS_DISPLAY_DEFAULTS for consistency.
 */
export const GEO_MARKER_FALLBACK_STYLES = {
  RADIUS: 3,
  FILL_COLOR: GEO_ANALYTICS_COLORS.DEFAULT,
  FILL_OPACITY: GEO_ANALYTICS_DISPLAY_DEFAULTS.opacity,
  COLOR: GEO_ANALYTICS_COLORS.DEFAULT,
  WEIGHT: 0,
  OPACITY: GEO_ANALYTICS_DISPLAY_DEFAULTS.opacity,
} as const;

/**
 * Demand heatmap layer multipliers and limits
 * Used for calculating radius and opacity when rendering demand markers
 */
export const GEO_DEMAND_HEATMAP_MULTIPLIERS = {
  RADIUS_MULTIPLIER: 2,
  OPACITY_MULTIPLIER: 1.1,
  MAX_OPACITY: 0.8,
} as const;

/**
 * Availability layer threshold labels and display strings
 */
export const GEO_AVAILABILITY_LABELS = {
  NO_PARTNER: "No Partner",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
} as const;

/**
 * Availability threshold display strings for UI
 * Based on new availability definition with thresholds: LOW < 30%, MEDIUM 30-70%, HIGH > 70%
 */
export const GEO_AVAILABILITY_THRESHOLD_LABELS = {
  NO_PARTNER: "No Partner Attached",
  HIGH: `High > ${GEO_AVAILABILITY_COLORS.THRESHOLDS.HIGH}%`,
  MEDIUM: `Medium ${GEO_AVAILABILITY_COLORS.THRESHOLDS.MEDIUM} - ${GEO_AVAILABILITY_COLORS.THRESHOLDS.HIGH}%`,
  LOW: `Low < ${GEO_AVAILABILITY_COLORS.THRESHOLDS.MEDIUM}%`,
} as const;

/**
 * Tailwind color classes mapping for availability colors
 */
export const GEO_AVAILABILITY_TAILWIND_CLASSES = {
  NO_PARTNER: "bg-red-900", // Dark red for no partner
  HIGH: "bg-green-500",
  MEDIUM: "bg-yellow-500",
  LOW: "bg-red-500",
} as const;

/**
 * Demand layer labels
 */
export const GEO_DEMAND_LABELS = {
  HIGH: "High",
  LOW: "Low",
} as const;

/**
 * Demand layer gradient colors for linear gradient
 */
export const GEO_DEMAND_GRADIENT = {
  HIGH: GEO_DEMAND_COLORS.HIGH,
  LOW: GEO_DEMAND_COLORS.LOW,
  STYLE: `linear-gradient(to right, ${GEO_DEMAND_COLORS.HIGH} 0%, ${GEO_DEMAND_COLORS.LOW} 100%)`,
} as const;

/**
 * Date preset filter options for supply planning
 * Backend converts these to actual date ranges
 */
export const GEO_DATE_PRESET_OPTIONS: Array<{ value: DatePreset; label: string }> = [
  { value: DatePreset.TODAY, label: "Today" },
  { value: DatePreset.TOMORROW, label: "Tomorrow" },
  { value: DatePreset.NEXT_5_DAYS, label: "Next 5 Days" },
  { value: DatePreset.CUSTOM, label: "Custom Date" },
] as const;

/**
 * Time preset filter options for supply planning
 * Backend converts these to slot indexes based on service category
 */
export const GEO_TIME_PRESET_OPTIONS: Array<{ value: TimePreset; label: string }> = [
  { value: TimePreset.WORKING_HOURS, label: "Working Hours" },
  { value: TimePreset.PEAK_HOURS, label: "Peak Hours" },
  { value: TimePreset.ULTRA_PEAK_HOURS, label: "Ultra Peak Hours" },
  { value: TimePreset.CUSTOM, label: "Custom Slots" },
] as const;


/**
 * Slot configuration constants for custom time preset
 */
export const GEO_SLOT_CONFIG = {
  /** Total number of slots in a day (48 slots = 24 hours * 2 slots per hour) */
  TOTAL_SLOTS: 48,
  /** Minutes per slot (30 minutes per slot) */
  MINUTES_PER_SLOT: 30,
  /** Minimum slot index */
  MIN_SLOT_INDEX: 0,
  /** Maximum slot index */
  MAX_SLOT_INDEX: 47,
  /** Number of slots per hour */
  SLOTS_PER_HOUR: 2,
} as const;

/**
 * Custom slot selector UI constants
 */
export const GEO_CUSTOM_SLOT_SELECTOR_CONFIG = {
  /** Number of columns in the slot grid */
  GRID_COLUMNS: 6,
  /** Gap between grid items (Tailwind gap-2) */
  GRID_GAP_CLASS: "gap-2",
  /** Maximum height of the slot selector container (Tailwind max-h-64) */
  MAX_HEIGHT_CLASS: "max-h-64",
  /** Padding for the slot selector container (Tailwind p-3) */
  CONTAINER_PADDING_CLASS: "p-3",
  /** Border radius for container (Tailwind rounded-md) */
  CONTAINER_BORDER_RADIUS_CLASS: "rounded-md",
  /** Border color class */
  BORDER_CLASS: "border border-gray-300",
  /** Grid columns class (Tailwind grid-cols-6) */
  GRID_COLUMNS_CLASS: "grid-cols-6",
  /** Spacing classes */
  SPACING_CLASSES: {
    /** Vertical spacing between children (Tailwind space-y-4) */
    VERTICAL_SPACING_4: "space-y-4",
    /** Margin top 3 (Tailwind mt-3) */
    MARGIN_TOP_3: "mt-3",
    /** Padding top 3 (Tailwind pt-3) */
    PADDING_TOP_3: "pt-3",
    /** Margin top 2 (Tailwind mt-2) */
    MARGIN_TOP_2: "mt-2",
    /** Margin bottom 2 (Tailwind mb-2) */
    MARGIN_BOTTOM_2: "mb-2",
    /** Border top (Tailwind border-t) */
    BORDER_TOP: "border-t",
  },
  /** Layout classes */
  LAYOUT_CLASSES: {
    /** Block display (Tailwind block) */
    BLOCK: "block",
    /** Overflow y auto (Tailwind overflow-y-auto) */
    OVERFLOW_Y_AUTO: "overflow-y-auto",
  },
  /** Text size classes */
  TEXT_SIZE_CLASSES: {
    /** Extra small text (Tailwind text-xs) */
    EXTRA_SMALL: "text-xs",
  },
  /** Color classes */
  COLOR_CLASSES: {
    /** Gray 600 text (Tailwind text-gray-600) */
    TEXT_GRAY_600: "text-gray-600",
    /** Gray 200 border (Tailwind border-gray-200) */
    BORDER_GRAY_200: "border-gray-200",
    /** Red 600 text (Tailwind text-red-600) */
    TEXT_RED_600: "text-red-600",
    /** Red 800 hover (Tailwind hover:text-red-800) */
    HOVER_RED_800: "hover:text-red-800",
  },
} as const;

/**
 * Slot time format constants
 */
export const GEO_SLOT_TIME_FORMAT = {
  /** Format for minutes when 0 */
  MINUTES_ZERO: "00",
  /** Format for minutes when 30 */
  MINUTES_THIRTY: "30",
} as const;

/**
 * Metric group types for organizing metrics display
 */
export enum MetricGroup {
  AVAILABILITY = "availability",
  PARTNERS = "partners",
  DEMAND = "demand",
  DAILY = "daily",
  OTHER = "other",
}

/**
 * Spacing constants for metrics display components
 */
export const GEO_METRICS_DISPLAY_SPACING = {
  COMPACT_VERTICAL: "space-y-2",
  NORMAL_VERTICAL: "space-y-4",
  GROUPED_VERTICAL: "space-y-4",
  PADDING_LEFT: "pl-4",
  PADDING: "p-8",
} as const;

/**
 * Hover card positioning and sizing constants
 */
export const GEO_HOVER_CARD_CONFIG = {
  OFFSET: 10,
  DEFAULT_WIDTH: 220,
  DEFAULT_HEIGHT: 160,
  DEFAULT_POSITION: "10px",
  Z_INDEX: GEO_MAP_Z_INDEX.HOVER_CARD,
} as const;

/**
 * Custom slot selector popover sizing constants
 */
export const GEO_CUSTOM_SLOT_POPOVER_CONFIG = {
  WIDTH: 300,
  MAX_HEIGHT: 400,
  PADDING: 3,
  SPACING: 2,
  WIDTH_CLASS: "w-[300px]",
  MAX_HEIGHT_CLASS: "max-h-[400px]",
  PADDING_CLASS: "p-3",
  MARGIN_BOTTOM_CLASS: "mb-3",
  PADDING_TOP_CLASS: "pt-3",
  MARGIN_TOP_CLASS: "mt-3",
} as const;

/**
 * Layer card styling constants
 */
export const GEO_LAYER_CARD_CONFIG = {
  PADDING_CLASS: "p-4",
  MARGIN_BOTTOM_CLASS: "mb-4",
  VERTICAL_SPACING_CLASS: "space-y-4",
  SECTION_SPACING_CLASS: "space-y-2",
  MARGIN_LEFT_CLASS: "ml-6",
  MARGIN_TOP_CLASS: "mt-1",
  MARGIN_BOTTOM_SMALL_CLASS: "mb-2",
  PADDING_TOP_CLASS: "pt-2",
  BORDER_TOP_CLASS: "border-t border-neutral-200",
  COLOR_SWATCH_SIZE_CLASS: "w-4 h-4",
  GRADIENT_HEIGHT_CLASS: "h-6",
} as const;

/**
 * Default values for supply planning form
 * Used to initialize form state and reset filters
 */
export const SUPPLY_PLANNING_DEFAULTS = {
  CITY_CODE: "",
  SUB_CITY_CODE: "",
  HUB_CODE: "",
  SERVICE_CATEGORY: EServiceCategory.WALKING,
  DATE_PRESET: DatePreset.TODAY,
  TIME_PRESET: TimePreset.WORKING_HOURS,
  ENTITY_TYPES: {
    HUBS: true,
    HOTSPOTS: true,
    CITIES: false,
    SUB_CITIES: false,
  },
  METRICS: {
    PARTNER_AVAILABILITY: false,
    CUSTOMER_DEMAND: false,
  },
} as const;